// PhotoQualityIndicator.js - Real-time photo quality feedback
import React, { useEffect, useState, useRef } from 'react';
import { Sun, SunDim, User, UserX, CheckCircle2, AlertCircle } from 'lucide-react';

export function PhotoQualityIndicator({
  webcamRef,
  isActive = true,
  className = ''
}) {
  const [lightingLevel, setLightingLevel] = useState('good'); // 'good', 'low', 'bright'
  const [faceDetected, setFaceDetected] = useState(false);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    if (!isActive || !webcamRef?.current) return;

    // Create hidden canvas for image analysis
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 240;
    const ctx = canvas.getContext('2d');

    const analyzeBrightness = () => {
      try {
        const video = webcamRef.current?.video;
        if (!video || video.readyState < 2) return;

        // Draw current frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Calculate average brightness (luminance)
        let totalBrightness = 0;
        for (let i = 0; i < data.length; i += 4) {
          // Luminance formula: 0.299*R + 0.587*G + 0.114*B
          const brightness = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          totalBrightness += brightness;
        }

        const avgBrightness = totalBrightness / (data.length / 4);

        // Determine lighting level
        if (avgBrightness < 50) {
          setLightingLevel('low');
        } else if (avgBrightness > 200) {
          setLightingLevel('bright');
        } else {
          setLightingLevel('good');
        }

        // Simple face detection using skin tone analysis in center region
        // Check center 40% of frame for skin-like pixels
        const centerStartX = Math.floor(canvas.width * 0.3);
        const centerEndX = Math.floor(canvas.width * 0.7);
        const centerStartY = Math.floor(canvas.height * 0.2);
        const centerEndY = Math.floor(canvas.height * 0.8);

        let skinPixels = 0;
        let totalCenterPixels = 0;

        for (let y = centerStartY; y < centerEndY; y++) {
          for (let x = centerStartX; x < centerEndX; x++) {
            const idx = (y * canvas.width + x) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];

            // Simple skin tone detection (works for various skin tones)
            // Based on RGB ranges common in skin colors
            const isSkinTone =
              r > 60 && r < 255 &&
              g > 40 && g < 230 &&
              b > 20 && b < 200 &&
              r > g && r > b &&
              Math.abs(r - g) > 10;

            if (isSkinTone) skinPixels++;
            totalCenterPixels++;
          }
        }

        const skinRatio = skinPixels / totalCenterPixels;
        // If more than 15% of center is skin-like, likely a face
        setFaceDetected(skinRatio > 0.15);

      } catch (err) {
        console.error('Photo quality analysis error:', err);
      }
    };

    // Run analysis every 500ms
    checkIntervalRef.current = setInterval(analyzeBrightness, 500);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isActive, webcamRef]);

  const getLightingIcon = () => {
    switch (lightingLevel) {
      case 'low':
        return <SunDim className="w-4 h-4 text-amber-500" />;
      case 'bright':
        return <Sun className="w-4 h-4 text-amber-500" />;
      default:
        return <Sun className="w-4 h-4 text-emerald-500" />;
    }
  };

  const getLightingText = () => {
    switch (lightingLevel) {
      case 'low':
        return 'Low light';
      case 'bright':
        return 'Too bright';
      default:
        return 'Good';
    }
  };

  return (
    <div className={`flex items-center gap-4 ${className}`}>
      {/* Lighting indicator */}
      <div className="flex items-center gap-1.5">
        {getLightingIcon()}
        <span className={`text-xs font-medium ${
          lightingLevel === 'good' ? 'text-emerald-600' : 'text-amber-600'
        }`}>
          {getLightingText()}
        </span>
      </div>

      {/* Face detection indicator */}
      <div className="flex items-center gap-1.5">
        {faceDetected ? (
          <>
            <User className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-medium text-emerald-600">Face detected</span>
          </>
        ) : (
          <>
            <UserX className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-medium text-amber-600">Position face</span>
          </>
        )}
      </div>

      {/* Overall quality indicator */}
      <div className="flex items-center gap-1.5">
        {lightingLevel === 'good' && faceDetected ? (
          <>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-medium text-emerald-600">Ready</span>
          </>
        ) : (
          <>
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-medium text-amber-600">Adjust</span>
          </>
        )}
      </div>
    </div>
  );
}

export default PhotoQualityIndicator;
